import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {getCompany} from '../../../shared/services/company.service';
import {ExternalCompanyDto} from '../../../shared/dtos/company.dto';
import {usePermissions} from '../../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../../shared/permissions/permissions';
import {formatApptTime} from '../../../shared/components/list-page/list-format';
import {
  ActionBar,
  CompanyBranding,
  HeaderButton,
  RecordMessage,
  RecordPage,
  RedButton,
  TitleRow,
  WideButtonRow,
} from '../../../shared/components/record/RecordKit';
import {ClientCard, ClientEditCard} from '../../client/profile/ClientCard';
import {useClientEditing} from '../../client/profile/useClientEditing';
import {addressLine, ContactEntry, CreateQuestion, ParsedAddress, withParsedAddress} from '../../client/profile/contact-entries';
import {getLocationFromAddress} from '../../../shared/misc/location-helpers';
import {getAppointment, updateAppointment} from '../appointment.service';
import {AppointmentDto, AppointmentStatus, AppointmentStatusType} from '../dto/appointment.dto';
import {parseApptTime} from './appointment-time';
import {AppointmentInfoCard} from './AppointmentInfoCard';
import '../../client/profile/ClientProfile.css';
import './AppointmentPage.css';

/*
 * The estimate appointment's own page (SQ-595). Android has no screen of its
 * own for it: it is the record screen opened on an appointment
 * (clients/ClientDetailScreen.kt, SQ-430) with the appointment section
 * (sections/appointment/AppointmentSectionView.kt and its controller), so
 * those are the spec; the layout is the record kit's (SQ-574).
 *
 * The header's status moves the appointment on when tapped. The bar holds
 * Cancel / Uncancel (no Delete). Below: the title, the company, CLIENT with
 * the client's card (the client page's, SQ-575), Appointment Info, and Start
 * an Estimate. Edit turns the client card into its edit card and Appointment
 * Info into the date and time buttons and the notes; Save writes the client.
 * The date, time and notes autosave as they change, in either mode.
 *
 * The card's address is the appointment's own (the estimate's job site), not
 * the client's: a visit can be at a rental or a second property, and that is
 * where Go must lead. Editing it moves the job site; the client's profile
 * keeps its address.
 */

/** Android waits this long after the last change before saving (AutosaveController). */
const AUTOSAVE_DELAY_MS = 600;

const NO_PICKS = {};

/** The appointment's job site as an address entry for the client card. */
const siteOf = (appointment: AppointmentDto): ContactEntry => ({
  key: 'record-address', rowId: null, kind: 'address', title: '',
  value: appointment.estimateAddress ?? '',
  city: appointment.estimateCity ?? '',
  state: appointment.estimateState ?? '',
  postalCode: appointment.estimatePostalCode ?? '',
});

const sameAddress = (a: ContactEntry, b: ContactEntry) =>
  a.value === b.value && a.city === b.city && a.state === b.state && a.postalCode === b.postalCode;

interface Point {lat: number; lng: number}

/** The header's word for the status (Android's chipGroupFor). Draft, Active and Pending Reschedule are all New. */
const statusLabel = (status: AppointmentStatusType): string => {
  switch (status) {
    case AppointmentStatus.Cancelled: return 'Canceled';
    case AppointmentStatus.Completed: return 'Completed';
    default: return 'New';
  }
};

/** Where a tap on the status takes it (Android's advanceStatus); nowhere from Completed or Canceled. */
const nextStatus = (status: AppointmentStatusType): AppointmentStatusType | undefined => {
  switch (status) {
    case AppointmentStatus.Draft:
    case AppointmentStatus.PendingReschedule:
      return AppointmentStatus.Active;
    case AppointmentStatus.Active:
      return AppointmentStatus.Completed;
    default:
      return undefined;
  }
};

/** Navigation state from whoever just created the appointment: it opens ready to edit (SQ-666). */
export interface AppointmentPageState {
  justCreated?: boolean;
}

export interface AppointmentPageProps {
  appointmentId: number;
  /** The appointment changed (status, date, client): the list behind the page is out of date. */
  onChanged: () => void;
}

const AppointmentPage = ({appointmentId, onChanged}: AppointmentPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {hasPermission} = usePermissions();
  // A fresh appointment opens in edit mode, as Android's becomeRecord does: there is nothing to view yet (SQ-666).
  const justCreated = (location.state as AppointmentPageState | null)?.justCreated === true;

  const [appointment, setAppointment] = useState<AppointmentDto>();
  const [loadError, setLoadError] = useState<string>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  // The appointment's own fields as they are being edited; the autosave sends these.
  const [time, setTime] = useState<Date>();
  const [notes, setNotes] = useState('');
  // The job site as it is being edited, and the point of a picked suggestion (else it is looked up on Save).
  const [site, setSite] = useState<ContactEntry>();
  const [sitePoint, setSitePoint] = useState<Point>();

  const [editing, setEditing] = useState(() => justCreated && hasPermission(PermissionCode.AppointmentsEdit));
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string>();
  const [infoOpen, setInfoOpen] = useState(true);
  // Only reference on a record, so it starts closed (the profile is where entries are worked on).
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const clientEditing = useClientEditing(appointment?.clientId, {editing, onError: setError, onChanged});
  const {client, fields, entries, contactsRead, rating} = clientEditing;

  // ---- Load ---------------------------------------------------------------------------------

  // Once read, the flag leaves the history entry: Back or a reload opens the appointment as any other.
  useEffect(() => {
    if (justCreated) navigate(location.pathname + location.search, {replace: true, state: {...(location.state as object), justCreated: undefined}});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    // The branding is furniture: a failure leaves it out.
    getCompany(navigate).then(c => active && setCompany(c)).catch(() => undefined);
    getAppointment(navigate, appointmentId)
      .then(loaded => {
        if (!active) return;
        setAppointment(loaded);
        setTime(parseApptTime(loaded.time));
        setNotes(loaded.notes ?? '');
        setSite(siteOf(loaded));
      })
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        logError('Error getting appointment', {appointmentId}, e as Error);
        setLoadError(`Couldn't load this appointment: ${e}`);
      });
    return () => { active = false; };
  }, [appointmentId, navigate]);

  // ---- Autosave: the date, time and notes ----------------------------------------------------

  const latest = useRef({time, notes});
  latest.current = {time, notes};
  const pending = useRef<number>();
  const changed = useRef(onChanged);
  changed.current = onChanged;

  /*
   * Every write of the appointment goes through this queue, one after
   * another. An autosave sends the time with the notes, so one still on the
   * wire when Cancel clears the time would put the time back if it landed
   * after it.
   */
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const enqueue = useCallback(<T, >(send: () => Promise<T>): Promise<T> => {
    // A failed write has told its own caller; it does not hold up the next.
    const next = queue.current.catch(() => undefined).then(send);
    queue.current = next;
    return next;
  }, []);

  const persist = useCallback(() => enqueue(async () => {
    // Read when it is sent, not when it was queued: a status change ahead of it may have moved the time.
    const now = latest.current;
    const updated = await updateAppointment(navigate, appointmentId, {time: now.time, notes: now.notes.trim()});
    setAppointment(current => current && {...current, updatedAt: updated.updatedAt});
    changed.current();
  }), [enqueue, navigate, appointmentId]);

  /** Writes a status change in turn, behind any autosave, and returns the appointment as the server has it. */
  const changeStatus = (status: AppointmentStatusType) => enqueue(async () => {
    const updated = await updateAppointment(navigate, appointmentId, {status});
    // The server may have cleared the time; an autosave queued behind this one must not send the old one.
    latest.current = {...latest.current, time: parseApptTime(updated.time)};
    return updated;
  });

  const persistQuietly = useCallback(() => {
    persist().catch(e => {
      if (isAuthRedirectError(e)) return;
      logError('Autosaving the appointment failed', {appointmentId}, e as Error);
      setError('Failed to save the appointment');
    });
  }, [persist, appointmentId]);

  const scheduleSave = () => {
    window.clearTimeout(pending.current);
    pending.current = window.setTimeout(() => {
      pending.current = undefined;
      persistQuietly();
    }, AUTOSAVE_DELAY_MS);
  };

  /**
   * Sends a waiting change now and waits for every save already on its way
   * (before Save, a status change, or leaving the page).
   */
  const flush = async () => {
    if (pending.current !== undefined) {
      window.clearTimeout(pending.current);
      pending.current = undefined;
      // Queued behind any save in flight, so awaiting it awaits them all.
      await persist();
      return;
    }
    // An autosave already sent reports its own failure.
    await queue.current.catch(() => undefined);
  };

  // Leaving the page with a change still waiting sends it.
  useEffect(() => () => {
    if (pending.current === undefined) return;
    window.clearTimeout(pending.current);
    pending.current = undefined;
    persistQuietly();
  }, [persistQuietly]);

  const changeTime = (next: Date) => {
    setError(undefined);
    setTime(next);
    scheduleSave();
  };

  const changeNotes = (next: string) => {
    setError(undefined);
    setNotes(next);
    scheduleSave();
  };

  // ---- Status, Cancel / Uncancel ---------------------------------------------------------------

  /** Runs one of the page's actions with the buttons held, showing `failure` if it throws. */
  const run = async (failure: string, action: () => Promise<void>) => {
    if (working) return;
    setWorking(true);
    setError(undefined);
    try {
      await action();
    } catch (e) {
      if (!isAuthRedirectError(e)) {
        logError(failure, {appointmentId}, e as Error);
        setError(failure);
      }
    } finally {
      setWorking(false);
    }
  };

  const advanceStatus = () => {
    const next = appointment && nextStatus(appointment.status);
    if (!next) return;
    void run('Failed to update the appointment status', async () => {
      await flush();
      setAppointment(await changeStatus(next));
      onChanged();
    });
  };

  /**
   * Cancel makes it Canceled; Uncancel puts it back to New. Either way the
   * page stays on the record and the server clears the date and time: a
   * canceled visit holds no slot, and an uncanceled one is scheduled fresh.
   */
  const setCancelled = (cancelled: boolean) =>
    void run(cancelled ? 'Failed to cancel the appointment' : 'Failed to uncancel the appointment', async () => {
      // A waiting or in-flight notes edit lands first, so the server's clearing comes after it.
      await flush();
      // New is Active on the server: what a fresh appointment gets.
      const updated = await changeStatus(cancelled ? AppointmentStatus.Cancelled : AppointmentStatus.Active);
      setAppointment(updated);
      setTime(parseApptTime(updated.time));
      onChanged();
    });

  const toggleCancel = () => {
    if (appointment?.status === AppointmentStatus.Cancelled) setCancelled(false);
    // SQ-745: Cancel always asks, scheduled or not, so one stray tap never cancels.
    else setConfirmCancel(true);
  };

  // ---- Edit / Save ------------------------------------------------------------------------------

  const canEdit = hasPermission(PermissionCode.AppointmentsEdit);
  const canEditClient = hasPermission(PermissionCode.ClientsEdit);

  const changeSite = (update: (entry: ContactEntry) => ContactEntry) => {
    setSite(current => current && update(current));
    // A typed change is looked up again on Save.
    setSitePoint(undefined);
  };

  const pickSite = (parsed: ParsedAddress) => {
    setSite(current => current && withParsedAddress(current, parsed));
    setSitePoint(parsed.lat !== undefined && parsed.lng !== undefined ? {lat: parsed.lat, lng: parsed.lng} : undefined);
  };

  /**
   * Writes an edited job site onto the appointment (its estimate). The server
   * takes an address only with its point: a picked suggestion's, else a fresh
   * lookup, else the point it had.
   */
  const saveSite = async () => {
    if (!appointment || !site || sameAddress(site, siteOf(appointment))) return;
    const point = sitePoint
      ?? await getLocationFromAddress(addressLine(site)).catch(() => null)
      ?? {lat: appointment.estimateLat, lng: appointment.estimateLng};
    const updated = await updateAppointment(navigate, appointmentId, {
      estimateAddress: site.value.trim(),
      estimateCity: site.city.trim(),
      estimateState: site.state.trim(),
      estimatePostalCode: site.postalCode.trim(),
      estimateLat: point.lat,
      estimateLng: point.lng,
    });
    const saved: AppointmentDto = {
      ...appointment, ...updated,
      estimateAddress: site.value.trim(), estimateCity: site.city.trim(),
      estimateState: site.state.trim(), estimatePostalCode: site.postalCode.trim(),
      estimateLat: point.lat, estimateLng: point.lng,
    };
    setAppointment(saved);
    setSite(siteOf(saved));
    setSitePoint(undefined);
  };

  // Leaving the page while editing commits the job site, as the client card's edits are.
  const leaving = useRef({editing, saveSite});
  leaving.current = {editing, saveSite};
  useEffect(() => () => {
    if (!leaving.current.editing) return;
    leaving.current.saveSite().catch(e => {
      if (!isAuthRedirectError(e)) logError('Saving the job site on leaving failed', {appointmentId}, e as Error);
    });
  }, [appointmentId]);

  const save = () => void run('Failed to save the appointment', async () => {
    await flush();
    if (canEdit) await saveSite();
    if (canEditClient && !(await clientEditing.saveAll())) return;
    setEditing(false);
    onChanged();
  });

  // ---- Start an Estimate ------------------------------------------------------------------------

  /**
   * The appointment moves forward into its estimate, which opens. It is then
   * finished (SQ-502): it leaves the appointments list, and the estimate is
   * the record from here on. What is being edited is saved first.
   */
  const startEstimate = () => void run('Failed to start the estimate', async () => {
    if (!appointment) return;
    await flush();
    if (editing && canEdit) await saveSite();
    if (editing && canEditClient && !(await clientEditing.saveAll())) return;
    if (appointment.status !== AppointmentStatus.Completed) {
      await changeStatus(AppointmentStatus.Completed);
    }
    clientEditing.settled.current = true;
    leaving.current.editing = false;
    onChanged();
    navigate(`/estimates/${appointment.estimateId}`);
  });

  // ---- The page ----------------------------------------------------------------------------------

  const ready = !!appointment && !!client && contactsRead;
  const failed = loadError ?? clientEditing.loadError;
  const cancelled = appointment?.status === AppointmentStatus.Cancelled;
  const canToggleCancel = cancelled ? canEdit : hasPermission(PermissionCode.AppointmentsCancel);
  const canStartEstimate = canEdit && hasPermission(PermissionCode.EstimatesCreate);
  const question: CreateQuestion = {client: fields, entries, prompt: null, picks: NO_PICKS};
  const card = {
    question,
    onPick: () => undefined,
    rating,
    onStar: clientEditing.tapStar,
    onSaveReview: clientEditing.saveReview,
    additionalOpen,
    onToggleAdditional: () => setAdditionalOpen(open => !open),
    recordAddress: site,
  };
  const scheduledFor = formatApptTime(time);

  return (
    <RecordPage
      name="AppointmentPage"
      status={appointment ? statusLabel(appointment.status) : ''}
      onStatusTap={appointment && canEdit && !working && nextStatus(appointment.status) ? advanceStatus : undefined}
      action={appointment && canEdit
        ? <HeaderButton label={editing ? 'Save' : 'Edit'} disabled={working}
                        onClick={() => { if (editing) save(); else setEditing(true); }}/>
        : undefined}
    >
      {failed && <RecordMessage error text={failed}/>}
      {!ready && !failed && <CircularProgress/>}
      {ready && appointment && <>
        {canToggleCancel && (
          <ActionBar>
            <RedButton label={cancelled ? 'Uncancel' : 'Cancel'} disabled={working} onClick={toggleCancel}/>
          </ActionBar>
        )}
        <TitleRow title={`Appointment #${appointment.estimateId}`}
                  dates={{createdAt: appointment.createdAt, updatedAt: appointment.updatedAt}}/>
        {company && <CompanyBranding name={company.name} logoUrl={company.logoUrl}/>}
        <div className="ap-client-heading">
          <span className="ap-client-label">CLIENT</span>
          <button type="button" className="ap-view-profile" onClick={() => navigate(`/clients/${appointment.clientId}`)}>
            View profile<span className="ap-chevron" aria-hidden="true">›</span>
          </button>
        </div>
        {editing && canEditClient
          ? <ClientEditCard {...card} onEntryChanged={clientEditing.changeEntry} onAddressPicked={clientEditing.pickAddress}
                            onTitleChanged={clientEditing.changeTitle}
                            onRecordAddressChanged={changeSite} onRecordAddressPicked={pickSite}
                            onAddEntry={kind => { clientEditing.addEntry(kind); setAdditionalOpen(true); }}/>
          : <ClientCard {...card}/>}
        <AppointmentInfoCard time={time} notes={notes} editing={editing} open={infoOpen}
                             onToggle={() => setInfoOpen(open => !open)} onTimeChanged={changeTime} onNotesChanged={changeNotes}/>
        {error && <div className="cc-error" role="alert">{error}</div>}
        {/* An appointment moves forward into an estimate, unless it is canceled: Uncancel it first. */}
        {!cancelled && canStartEstimate && (
          <div className="ap-actions">
            {working ? <CircularProgress/> : (
              <WideButtonRow>
                <button type="button" className="cc-create-button" onClick={startEstimate}>Start an Estimate</button>
              </WideButtonRow>
            )}
          </div>
        )}
      </>}

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>Cancel this appointment?</DialogTitle>
        {scheduledFor && (
          <DialogContent>
            <DialogContentText>It's scheduled for {scheduledFor}.</DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)}>Keep it</Button>
          <Button variant="contained" color="error" onClick={() => { setConfirmCancel(false); setCancelled(true); }}>
            Cancel appointment
          </Button>
        </DialogActions>
      </Dialog>
    </RecordPage>
  );
};

export default AppointmentPage;
